// Versão: 1.02
// Data..: 2026-10-13
// v1.02: ícone do kind 'agenda' (compromisso em breve), com navegação para /schedule.
// v1.01: ícone do kind 'cadastro' e navegação por linkTo (alerta de cadastro incompleto).
// Sino de notificações no topbar: contador de não-lidas + dropdown com a lista.
// Equipe: novos chamados/WhatsApp/tarefas. Cliente: atualizações nos próprios chamados.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@makweb/shared';
import { useAuth } from '../context/AuthContext.js';
import { useNotifications, type AppNotification } from '../lib/useNotifications.js';

const KIND_ICON: Record<AppNotification['kind'], string> = {
  ticket: '🎫',
  whatsapp: '💬',
  task: '✅',
  update: '🔔',
  cadastro: '⚠',
  agenda: '📅',
};

// Formata "há X min/h/d" de forma curta.
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
}

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const { notifications, unreadCount, markAllRead, markRead, clearAll } = useNotifications(true, user?.id, isStaff);

  // Fecha ao clicar fora.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    setOpen((v) => {
      // Ao abrir, marca tudo como lido (o usuário viu).
      if (!v && unreadCount) markAllRead();
      return !v;
    });
  };

  const openNotification = (n: AppNotification) => {
    markRead(n.id);
    setOpen(false);
    // Destino explícito (ex.: alerta de cadastro) tem prioridade; senão, chamado/WhatsApp.
    if (n.linkTo) navigate(n.linkTo);
    else navigate(n.kind === 'whatsapp' ? '/whatsapp' : `/tickets/${n.ticketId}`);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-icon btn-ghost" onClick={toggle} title="Notificações" aria-label="Notificações" style={{ position: 'relative' }}>
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px',
              borderRadius: 999, background: 'var(--danger)', color: '#fff', fontSize: 10, fontWeight: 700,
              display: 'grid', placeItems: 'center', lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="card"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 340, maxHeight: 460,
            overflow: 'hidden', padding: 0, zIndex: 50, boxShadow: 'var(--shadow-lg)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div className="flex items-center justify-between" style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <strong style={{ fontSize: 14 }}>Notificações</strong>
            {notifications.length > 0 && (
              <button className="btn btn-sm btn-ghost" onClick={clearAll} style={{ fontSize: 12 }}>Limpar</button>
            )}
          </div>
          <div style={{ overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div className="empty" style={{ padding: 28 }}>
                <div className="empty-icon">🔕</div>
                Sem notificações por enquanto.
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', textAlign: 'left',
                    padding: '11px 14px', border: 'none', cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: n.read ? 'transparent' : 'var(--brand-soft)',
                  }}
                >
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{KIND_ICON[n.kind]}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.title}</div>
                    <div className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.body}</div>
                    <div className="text-dim" style={{ fontSize: 11, marginTop: 2 }}>{timeAgo(n.createdAt)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
